import { EVENTS, OFFLINE, ticketedVasDetailNames } from '@/src/constants/itinerary/insurance'
import { VasAncillary, VasDetail } from '@/src/types/itinerary'

/** Used when the product does not declare `maxTickets`. */
export const DEFAULT_MAX_TICKETS = 10

export const getTicketedVasDetail = (vas?: VasAncillary | null): VasDetail | undefined => vas?.vasDetails?.[0]

/**
 * The detail returned by the API is priced for the quantity it already carries
 * (one ticket when nothing is preselected), so unit values are derived from it.
 */
const getUnitAmount = (total: number, quantity: number) => (quantity > 0 ? total / quantity : total)

export const getTicketUnitPrice = (vas?: VasAncillary | null) => {
    const detail = getTicketedVasDetail(vas)
    const { pricePerTicket, totalPremiumAmount = 0, quantity } = detail || {}
    if (typeof pricePerTicket === 'number' && pricePerTicket > 0) return pricePerTicket
    return getUnitAmount(totalPremiumAmount || 0, Number(quantity) || 0)
}

export const getTicketUnitVat = (vas?: VasAncillary | null) => {
    const detail = getTicketedVasDetail(vas)
    const { vatAmount = 0, quantity } = detail || {}
    return getUnitAmount(vatAmount || 0, Number(quantity) || 0)
}

/**
 * What the traveller is actually charged for one ticket: the backend's premium plus its VAT,
 * which is how the fare breakup totals it too.
 */
export const getTicketUnitPriceWithVat = (vas?: VasAncillary | null) =>
    getTicketUnitPrice(vas) + getTicketUnitVat(vas)

export const getMaxTickets = (vas?: VasAncillary | null) => {
    const { maxTickets } = getTicketedVasDetail(vas) || {}
    return typeof maxTickets === 'number' && maxTickets > 0 ? maxTickets : DEFAULT_MAX_TICKETS
}

export const getTicketQuantity = (vas?: VasAncillary | null) => Number(getTicketedVasDetail(vas)?.quantity) || 0

/**
 * A copy of the product priced for `quantity`, shaped so the shared pricing/payload code
 * (which only reads `vasDetails[0].totalPremiumAmount` / `vatAmount`) needs no special casing.
 */
export const buildTicketedVasSelection = (vas: VasAncillary, quantity: number): VasAncillary => {
    const detail = getTicketedVasDetail(vas)
    const unitPrice = getTicketUnitPrice(vas)
    const unitVat = getTicketUnitVat(vas)

    return {
        ...vas,
        vasDetails: [
            {
                ...(detail as VasDetail),
                quantity,
                pricePerTicket: unitPrice,
                totalPremiumAmount: unitPrice * quantity,
                vatAmount: unitVat * quantity,
                // Both spellings: older VAS payloads use `selected`, the events payload `isSelected`.
                selected: quantity > 0,
                isSelected: quantity > 0
            },
            ...(vas.vasDetails?.slice(1) || [])
        ]
    }
}

/**
 * Gulf Cup and friends arrive under the generic `EVENTS` type, so all three of
 * vasType / provider / detail `name` have to match before the stepper UI claims them.
 */
export const isTicketedVas = (vas?: VasAncillary | null) => {
    if (!vas || vas.vasType !== EVENTS) return false
    const detail = getTicketedVasDetail(vas)
    const provider = detail?.provider || vas.vasProvider
    return provider === OFFLINE && ticketedVasDetailNames.includes(detail?.name || '')
}
